import { readonly, ref, type Ref } from 'vue'

import type { Tone } from './types'

/**
 * Toasty.
 *
 * Toast je vyhradený pre výsledok akcie, ktorú používateľ nevidí na obrazovke
 * (odoslaná objednávka, publikovaný cenník, chyba uloženia). Pre maličkosti sa
 * podľa MASTER.md §5 nepoužíva — inak si na neho používateľ zvykne a prehliadne
 * ten dôležitý.
 */
export interface ToastMessage {
	readonly id: string
	readonly titleSk: string
	readonly descriptionSk?: string
	readonly tone: Tone
	/** Chyba zostáva, kým ju používateľ nezavrie — nesmie ujsť. */
	readonly durationMs: number
}

export interface ToastInput {
	titleSk: string
	descriptionSk?: string
	tone?: Tone
	durationMs?: number
}

const DEFAULT_DURATION_MS = 5_000

const items = ref<ToastMessage[]>([])

export function useToast(): {
	items: Readonly<Ref<readonly ToastMessage[]>>
	push: (input: ToastInput) => string
	success: (titleSk: string, descriptionSk?: string) => string
	error: (titleSk: string, descriptionSk?: string) => string
	dismiss: (id: string) => void
} {
	function push(input: ToastInput): string {
		const id = globalThis.crypto.randomUUID()
		items.value = [
			...items.value,
			{
				id,
				titleSk: input.titleSk,
				...(input.descriptionSk === undefined ? {} : { descriptionSk: input.descriptionSk }),
				tone: input.tone ?? 'neutral',
				durationMs: input.durationMs ?? (input.tone === 'danger' ? 0 : DEFAULT_DURATION_MS),
			},
		]

		return id
	}

	function dismiss(id: string): void {
		items.value = items.value.filter((item) => item.id !== id)
	}

	return {
		items: readonly(items) as Readonly<Ref<readonly ToastMessage[]>>,
		push,
		success: (titleSk, descriptionSk) => push({ titleSk, ...(descriptionSk === undefined ? {} : { descriptionSk }), tone: 'success' }),
		error: (titleSk, descriptionSk) => push({ titleSk, ...(descriptionSk === undefined ? {} : { descriptionSk }), tone: 'danger' }),
		dismiss,
	}
}
